import { Subscribable, Subscription } from '@benzinga/subscribable';
import { PriceAlertsRequest, PriceAlertsRequestEvent } from './request';
import { PriceAlertsStore, PriceAlertsStoreEvent } from './store';
import { SafePromise } from '@benzinga/safe-await';
import { Session } from '@benzinga/session';
import { oneRequestAtATime } from '@benzinga/utils';
import { PriceAlerts } from './proto/price_alerts_protos';

export type PriceAlertsManagerEvent = PriceAlertsStoreEvent | PriceAlertsRequestEvent;

export class PriceAlertsManager extends Subscribable<PriceAlertsManagerEvent> {
  private store: PriceAlertsStore;
  private request: PriceAlertsRequest;
  private storeSubscription?: Subscription<PriceAlertsStore>;
  private requestSubscription?: Subscription<PriceAlertsRequest>;

  private fetchPriceAlerts = oneRequestAtATime(async () => {
    const alerts = await this.request.getPriceAlerts();
    if (alerts.err) {
      return alerts;
    } else {
      this.store.setPriceAlerts(alerts.ok);
      return alerts;
    }
  });

  constructor(session: Session) {
    super();
    this.store = new PriceAlertsStore();
    this.request = new PriceAlertsRequest(session);
  }

  public static getName = () => 'benzinga-price-alerts';

  public getPriceAlerts = async (force = false): SafePromise<Partial<PriceAlerts.Alert>[]> => {
    if (force || this.store.shouldWeFetchAlerts() === true) {
      return await this.fetchPriceAlerts();
    } else {
      return { ok: this.store.getPriceAlerts() as PriceAlerts.Alert[] };
    }
  };

  public addPriceAlert = async (
    data: Partial<PriceAlerts.PostAlertRequest>,
  ): SafePromise<Partial<PriceAlerts.PostAlertResponse>> => {
    const result = await this.request.addPriceAlert(data);
    if (result.err) {
      return result;
    }
    // refresh cache after successful operation
    await this.refreshCache();
    return result;
  };

  public updatePriceAlert = async (
    data: Partial<PriceAlerts.PostAlertRequest>,
  ): SafePromise<Partial<PriceAlerts.PostAlertResponse>> => {
    const result = await this.request.updatePriceAlert(data);
    if (result.err) {
      return result;
    }
    // refresh cache after successful operation
    await this.refreshCache();
    return result;
  };

  public deletePriceAlert = async (guid: string): SafePromise<Response> => {
    const result = await this.request.deletePriceAlert(guid);
    if (result.err) {
      return result;
    }
    // refresh cache after successful operation
    await this.refreshCache();
    return result;
  };

  public getStoredPriceAlerts = (): Partial<PriceAlerts.Alert>[] | undefined => this.store.getPriceAlerts();

  protected onFirstSubscription(): void {
    this.storeSubscription = this.store.listen(event => this.dispatch(event));
    this.requestSubscription = this.request.listen(event => this.dispatch(event));
  }

  protected onZeroSubscriptions(): void {
    this.requestSubscription?.unsubscribe();
    this.storeSubscription?.unsubscribe();
  }

  /**
   * Refreshes the cache by fetching latest data from server
   * This ensures all subscribers get updated data immediately
   */
  private refreshCache = async (): SafePromise<void> => {
    await this.fetchPriceAlerts();
    return { ok: undefined };
  };
}
